import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { toast } from 'sonner';
import { Plus as PlusIcon, Trash as TrashIcon } from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Action, Subject } from '@erp/authz';
import type { ParsePriceListUploadResponse } from '@erp/rpc';
import { cn } from '@/lib/utils';
import { useCan } from 'src/hooks/useCan';
import {
  useCreateDiscountGroupV2,
  useParsePriceListUploadV2,
} from '@/api/rpc/hooks/accounts-receivable/useDiscountGroupsV2';
import { CustomersAutocomplete } from '@/components/customer/CustomersAutocomplete';
import {
  ElementRowCells,
  defaultEffectiveEnd,
  draftToElementInput,
  elementIssue,
  emptyElement,
  useSegmentOptions,
  type ElementDraft,
} from './elementRowShared';
import {
  EMPTY_PRICE_LIST_UPLOAD,
  UploadPriceListPanel,
  isPriceListUploadReady,
  priceListColumnMapping,
  type PriceListUploadState,
} from './UploadPriceListPanel';

/** How elements enter the group: typed into the table, or read from a sheet. */
type ElementsMode = 'manual' | 'upload';

/** Seed for a prefilled fixed-price element row (bulk-create flows). */
export interface CreateGroupSeedElement {
  productVariantId: string;
  sku: string;
  productName?: string;
  fixedPrice: number;
}

/**
 * Create a Discounts v2 group: shared header (name, customer, dates) + the
 * elements, either typed into the table one row at a time or read from an
 * uploaded spreadsheet (auto-matched by the AI parser or by explicitly picked
 * columns). Groups can be saved as DRAFT or submitted straight to approval.
 * The generated discount code covers identification, so there is no manual
 * reference-code field. Bulk flows (margin simulator, SO v3 review) prefill
 * the form via the `initial*` props (which hide the upload mode); the seed
 * re-applies each time the dialog opens.
 */
export function CreateGroupDialog({
  open,
  onOpenChange,
  initialName,
  initialCustomerId,
  initialElements,
  onCreated,
  onUploaded,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialName?: string;
  initialCustomerId?: string | null;
  initialElements?: CreateGroupSeedElement[];
  /** Fired after the group is created (before the dialog closes). */
  onCreated?: (result: { discountGroupId: string; status: string }) => void;
  /** Fired after an uploaded list is parsed into a group (dialog closes). */
  onUploaded?: (result: ParsePriceListUploadResponse) => void;
}) {
  const t = useGT();
  const modeOptions = [
    {
      value: 'manual',
      label: t('Enter manually', { context: 'discount elements entry mode' }),
    },
    {
      value: 'upload',
      label: t('Upload list', { context: 'discount elements entry mode' }),
    },
  ] as const;
  const create = useCreateDiscountGroupV2();
  const parseUpload = useParsePriceListUploadV2();
  // Approve-RBAC holders create groups directly approved — no submit step.
  const [canApprove] = useCan(Action.Approve, Subject.DiscountV2);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [effectiveStart, setEffectiveStart] = useState('');
  // Discounts must expire: default to year end, editable.
  const [effectiveEnd, setEffectiveEnd] = useState(defaultEffectiveEnd);
  const [mode, setMode] = useState<ElementsMode>('manual');
  const [uploadState, setUploadState] = useState<PriceListUploadState>(
    EMPTY_PRICE_LIST_UPLOAD,
  );
  const [elements, setElements] = useState<ElementDraft[]>([]);
  // Experimental tabular quick-add row (margins-table style) — filled out and
  // committed to the list with the + button.
  const [quickRow, setQuickRow] = useState<ElementDraft>(emptyElement());

  // Re-seed from the initial* props on every open transition (render-time
  // state adjustment, no effect). Callers without seeds keep the old
  // behavior: state persists across a cancel/reopen.
  const hasSeed =
    initialName != null || initialCustomerId != null || initialElements != null;
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open && hasSeed) {
      setName(initialName ?? '');
      setCustomerId(initialCustomerId ?? null);
      setElements(
        (initialElements ?? []).map((seed) => ({
          ...emptyElement(),
          discountValue: String(seed.fixedPrice),
          targetValue: seed.productVariantId,
          targetLabel: seed.sku,
          targetDescription: seed.productName ?? '',
        })),
      );
      setQuickRow(emptyElement());
      setMode('manual');
    }
  }
  // Seeded rows come from another screen — uploading a sheet on top of them
  // makes no sense, so the mode toggle is hidden.
  const canUpload = initialElements == null;
  const uploading = mode === 'upload';

  // Same classification source as the Product Catalog filters — segment
  // targets suggest real PRODVIEW values instead of free text.
  const segmentOptions = useSegmentOptions();

  function patchQuickRow(patch: Partial<ElementDraft>) {
    setQuickRow((prev) => ({ ...prev, ...patch }));
  }

  function addQuickRow() {
    if (elementIssue(quickRow)) return;
    setElements((prev) => [...prev, quickRow]);
    setQuickRow(emptyElement());
  }

  function patchElement(key: number, patch: Partial<ElementDraft>) {
    setElements((prev) =>
      prev.map((el) => (el.key === key ? { ...el, ...patch } : el)),
    );
  }

  // ── Single use select-all (mirrors EditableElementsTable, local-only) ────
  const singleUseValues = elements.map((el) => el.isSingleUse);
  const allSingleUse =
    singleUseValues.length > 0 && singleUseValues.every(Boolean);
  const someSingleUse = singleUseValues.some(Boolean);
  const singleUseSelectAllState: boolean | 'indeterminate' = allSingleUse
    ? true
    : someSingleUse
      ? 'indeterminate'
      : false;

  /** Toggle single-use on every committed row (and the quick-add row). */
  function applySingleUseAll(next: boolean) {
    setElements((prev) => prev.map((el) => ({ ...el, isSingleUse: next })));
    setQuickRow((prev) => ({ ...prev, isSingleUse: next }));
  }

  // A filled-but-uncommitted quick-add row still counts: filling the top row
  // and clicking Create must work without pressing +.
  const pendingQuickRow = elementIssue(quickRow) ? null : quickRow;
  const allElements = pendingQuickRow
    ? [...elements, pendingQuickRow]
    : elements;

  const blockers: string[] = [];
  // Uploaded lists take their name from the sheet when left blank.
  if (!uploading && !name.trim())
    blockers.push(
      t('Name is required.', { context: 'discount group save blocker' }),
    );
  // Every discount group must be scoped to a customer — org-wide ("all
  // customers") groups are not allowed from this dialog.
  if (!customerId)
    blockers.push(
      t('Customer is required.', { context: 'discount group save blocker' }),
    );
  if (uploading) {
    if (!uploadState.documentId)
      blockers.push(
        t('Upload a spreadsheet.', { context: 'discount group save blocker' }),
      );
    else if (!isPriceListUploadReady(uploadState))
      blockers.push(
        t('Pick the part number and price columns.', {
          context: 'discount group save blocker',
        }),
      );
  } else {
    if (allElements.length === 0)
      blockers.push(
        t('Add at least one element.', {
          context: 'discount group save blocker',
        }),
      );
    const badElements = elements.filter((el) => elementIssue(el));
    if (badElements.length > 0)
      blockers.push(
        t('{count} element(s) are incomplete.', {
          context: 'discount group save blocker',
          count: String(badElements.length),
        }),
      );
  }
  if (!effectiveEnd)
    blockers.push(
      t('An end date is required.', { context: 'discount group save blocker' }),
    );
  else if (effectiveStart && effectiveEnd < effectiveStart)
    blockers.push(
      t('End date must be on or after the start date.', {
        context: 'discount group save blocker',
      }),
    );
  const busy = create.isPending || parseUpload.isPending;
  const canSubmit = blockers.length === 0 && !busy;

  function reset() {
    setName('');
    setDescription('');
    setCustomerId(null);
    setEffectiveStart('');
    setEffectiveEnd(defaultEffectiveEnd());
    setElements([]);
    setQuickRow(emptyElement());
    setMode('manual');
    setUploadState(EMPTY_PRICE_LIST_UPLOAD);
  }

  function submitUpload(mode: 'draft' | 'submit' | 'approve') {
    if (!canSubmit || !uploadState.documentId || !effectiveEnd) return;
    parseUpload.mutate(
      {
        documentId: uploadState.documentId,
        sheetName: uploadState.sheetName ?? undefined,
        name: name.trim() || null,
        description: description.trim() || null,
        customerId,
        effectiveStart: effectiveStart || null,
        effectiveEnd,
        columnMapping: priceListColumnMapping(uploadState),
        submitForApproval: mode === 'submit',
        approveImmediately: mode === 'approve',
      },
      {
        onSuccess: (result) => {
          onUploaded?.(result);
          onCreated?.(result);
          reset();
          onOpenChange(false);
        },
        onError: (err) =>
          toast.error(
            err instanceof Error
              ? err.message
              : t('Could not parse the price list'),
          ),
      },
    );
  }

  function submit(mode: 'draft' | 'submit' | 'approve') {
    if (!canSubmit) return;
    if (uploading) {
      submitUpload(mode);
      return;
    }
    create.mutate(
      {
        name: name.trim(),
        description: description.trim() || null,
        // The generated discount code covers identification — no manual ref.
        referenceCode: null,
        customerId,
        elements: allElements.map((el) => ({
          ...draftToElementInput(el),
          // Dialog dates are group-level: they apply to every element.
          effectiveStart: effectiveStart || null,
          effectiveEnd,
        })),
        submitForApproval: mode === 'submit',
        approveImmediately: mode === 'approve',
      },
      {
        onSuccess: (result) => {
          toast.success(
            mode === 'approve'
              ? t('Discount group created and approved')
              : mode === 'submit'
                ? t('Discount group submitted for approval')
                : t('Discount group saved as draft'),
          );
          onCreated?.(result);
          reset();
          onOpenChange(false);
        },
        onError: (err) =>
          toast.error(
            err instanceof Error ? err.message : t('Could not create group'),
          ),
      },
    );
  }

  return (
    // The dialog stays up while an upload parses — closing mid-flight would
    // leave the mutation running with nowhere to report.
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (busy) return;
        onOpenChange(next);
      }}
      disableBackdropClickClose={busy}
      disableEscapeKeyClose={busy}
    >
      <DialogContent
        className="flex max-h-[85vh] flex-col gap-0 p-0 sm:max-w-[60rem]"
        showCloseButton={!busy}
      >
        <DialogHeader className="shrink-0 px-6 pt-6 pb-2">
          <DialogTitle>
            <T>New Discount Group</T>
          </DialogTitle>
          <DialogDescription>
            <T>
              A basket of discount elements created together — an exception
              price list, a bulk batch, or a discount request. Elements activate
              when the group is approved.
            </T>
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4">
          <div>
            <Label className="text-xs" htmlFor="dg-name">
              {uploading ? (
                <T context="uploaded list: name defaults from the sheet">
                  Name (optional)
                </T>
              ) : (
                <T>Name</T>
              )}
            </Label>
            <Input
              id="dg-name"
              className="mt-1"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div>
            <Label className="text-xs">
              <T>Customer</T>
            </Label>
            <CustomersAutocomplete
              selectedCustomerId={customerId ?? undefined}
              onCustomerSelect={(id) => setCustomerId(id ?? null)}
              allowClear
              className="mt-1 w-full"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs" htmlFor="dg-start">
                <T>Effective start (optional)</T>
              </Label>
              <Input
                id="dg-start"
                type="date"
                className="mt-1"
                value={effectiveStart}
                onChange={(e) => setEffectiveStart(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs" htmlFor="dg-end">
                <T context="discount group required end date label">
                  Effective end
                </T>
              </Label>
              <Input
                id="dg-end"
                type="date"
                className="mt-1"
                required
                value={effectiveEnd}
                onChange={(e) => setEffectiveEnd(e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label className="text-xs" htmlFor="dg-desc">
              <T>Description (optional)</T>
            </Label>
            <Textarea
              id="dg-desc"
              className="mt-1"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Elements: either a single editable table (the top row is the
              blank add-row — + commits it; committed elements stack below
              with a trash to remove) or an uploaded spreadsheet. */}
          <div className="space-y-2">
            <div className="text-sm font-medium">
              <T context="discount group elements section title">Elements</T>
            </div>
            {canUpload && (
              <div className="inline-flex rounded-md border p-0.5">
                {modeOptions.map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={mode === value}
                    disabled={busy}
                    onClick={() => setMode(value)}
                    className={cn(
                      'rounded px-3 py-1 text-xs font-medium transition-colors',
                      mode === value
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            {uploading ? (
              <UploadPriceListPanel
                state={uploadState}
                onChange={setUploadState}
                disabled={busy}
              />
            ) : (
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-xs">
                  <thead className="text-left whitespace-nowrap text-muted-foreground">
                    <tr className="border-b">
                      <th className="w-8 px-2 text-center font-medium">#</th>
                      <th className="p-2 font-medium">
                        <T>Target type</T>
                      </th>
                      <th className="min-w-56 p-2 font-medium">
                        <T>Target</T>
                      </th>
                      <th className="p-2 font-medium">
                        <T>Discount type</T>
                      </th>
                      <th className="p-2 font-medium">
                        <T>Value</T>
                      </th>
                      <th className="p-2 font-medium">
                        <T>Max qty</T>
                      </th>
                      <th className="p-2 text-center font-medium">
                        <span className="inline-flex items-center gap-1.5">
                          <Checkbox
                            checked={singleUseSelectAllState}
                            disabled={elements.length === 0}
                            aria-label={t('Toggle single use on all elements', {
                              context: 'discount elements table select-all',
                            })}
                            onCheckedChange={(v) =>
                              applySingleUseAll(v === true)
                            }
                          />
                          <T>Single use</T>
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* key: remount on reset so the formatted inputs clear.
                      Enter commits the row like the + button; comboboxes and
                      selects preventDefault their own Enter handling, so the
                      defaultPrevented check avoids hijacking option picks. */}
                    <tr
                      key={quickRow.key}
                      className="border-b"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.defaultPrevented) {
                          e.preventDefault();
                          addQuickRow();
                        }
                      }}
                    >
                      <td className="w-8 px-1 py-1.5">
                        <Button
                          type="button"
                          size="icon"
                          className="size-6"
                          disabled={!!elementIssue(quickRow)}
                          title={t('Add this discount to the list', {
                            context: 'discount elements add row button',
                          })}
                          onClick={addQuickRow}
                        >
                          <PlusIcon size={14} />
                        </Button>
                      </td>
                      <ElementRowCells
                        row={quickRow}
                        onPatch={patchQuickRow}
                        segmentOptions={segmentOptions}
                      />
                    </tr>
                    {elements.map((el, index) => (
                      <tr
                        key={el.key}
                        className="group border-b last:border-b-0"
                      >
                        {/* Fixed-size cell: row number at rest, trash on hover. */}
                        <td className="w-8 px-1 py-1.5">
                          <span className="flex size-6 items-center justify-center text-muted-foreground tabular-nums group-hover:hidden">
                            {index + 1}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="hidden size-6 text-destructive group-hover:flex"
                            title={t('Remove this discount', {
                              context: 'discount elements remove row button',
                            })}
                            onClick={() =>
                              setElements((prev) =>
                                prev.filter((e2) => e2.key !== el.key),
                              )
                            }
                          >
                            <TrashIcon size={14} />
                          </Button>
                        </td>
                        <ElementRowCells
                          row={el}
                          onPatch={(patch) => patchElement(el.key, patch)}
                          segmentOptions={segmentOptions}
                        />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {blockers.length > 0 && (
          // Pinned above the footer so the reasons the group can't be saved stay
          // visible while the body scrolls.
          <ul className="shrink-0 list-inside list-disc border-t bg-muted/40 px-6 py-2 text-xs text-muted-foreground">
            {blockers.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
        )}

        <DialogFooter className="shrink-0 border-t px-6 py-4">
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() => onOpenChange(false)}
          >
            <T>Cancel</T>
          </Button>
          <Button
            variant="outline"
            disabled={!canSubmit}
            onClick={() => submit('draft')}
          >
            <T>Save As Draft</T>
          </Button>
          {canApprove ? (
            <Button disabled={!canSubmit} onClick={() => submit('approve')}>
              {busy ? <T>Saving…</T> : <T>Create Discount Group</T>}
            </Button>
          ) : (
            <Button disabled={!canSubmit} onClick={() => submit('submit')}>
              {busy ? <T>Saving…</T> : <T>Submit For Approval</T>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
